import * as Sentry from '@sentry/browser';
import { useContext, useEffect, useRef } from 'react';
import { useEventBus } from '../../util/EventBus';
import { AnalyticsContext } from '../AnalyticsContext';

/** Observes analytics events and assigns a subset of their properties as sentry tags,
 * to provide extra information on captured errors.
 *
 * @requires <AnalyticsProvider> as an ancestor in the tree. */
export const SentryTagDispatcher = () => {
  const { bus } = useContext(AnalyticsContext);
  const tagState = useRef(new Map<string, string>());

  useEventBus(bus, ({ data }) => {
    const { bookingData, pageData, searchData } = data;

    const currentData = {
      'bookingData.bookingForm': bookingData?.bookingForm,
      'bookingData.bookingSpecialty': bookingData?.bookingSpecialty,
      'bookingData.process': bookingData?.process,
      'pageData.section': pageData?.section,
      'pageData.pageType': pageData?.pageType,
      'pageData.subsection': pageData?.subsection,
      'search.specialty': searchData?.specialty,
      'search.location.suburb': searchData?.filtersApplied?.location?.suburb,
      'search.location.postcode': searchData?.filtersApplied?.location?.postcode,
      'search.location.state': searchData?.filtersApplied?.location?.state,
      'search.location.region': searchData?.filtersApplied?.location?.region,
    };

    /** Sentry allows us to bulk-set tags, but since this is happening upon every analytics event,
     * we diff the new tags with the old ones to only apply the needed changes.
     */
    for (const [key, value] of Object.entries(currentData)) {
      if (tagState.current.get(key) === value) continue;

      if (value === undefined) {
        tagState.current.delete(key);
        Sentry.setTag(key, undefined);
      } else {
        tagState.current.set(key, value);
        Sentry.setTag(key, value);
      }
    }
  });

  /** Upon unmounting, we clean up all tags we currently have applied */
  useEffect(
    () => () => {
      tagState.current.forEach((_, key) => {
        Sentry.setTag(key, undefined);
      });
    },
    [],
  );

  return null;
};
